import { z } from 'zod';

import { PayService, validKidModulo } from '@/domain/pay';
import { TranslationsFullPath } from '@/domain/translations';
import { isValidAccountNumber } from '@/utils/account-utils';
import { sanitizeNumericValue } from '@/utils/text-format-utils';

const payeeAccountNumberInvalidPath: TranslationsFullPath = 'pay.validation.payeeAccountNumber.invalid';
const payeeAccountNumberRequiredPath: TranslationsFullPath = 'pay.validation.payeeAccountNumber.required';
const payeeAccountNumberInvalidOcrPath: TranslationsFullPath = 'pay.validation.payeeAccountNumber.ocrInvalid';

const PayeeAccountNumberSchema = z
    .string({ message: payeeAccountNumberRequiredPath })
    .min(1, payeeAccountNumberRequiredPath)
    .refine((val) => {
        const sanitized = sanitizeNumericValue(val);
        const valid = isValidAccountNumber(sanitized);
        return valid;
    }, payeeAccountNumberInvalidPath);

const kidIllegalLengthPath: TranslationsFullPath = 'pay.validation.kid.illegalLength';
const kidNotValidPath: TranslationsFullPath = 'pay.validation.kid.invalid';
const kidRequiredPath: TranslationsFullPath = 'pay.validation.kid.required';

export const KidSchema = z
    .string()
    .refine((kid) => kid.length >= 2 && kid.length <= 25, {
        message: kidIllegalLengthPath,
    })
    .refine((kid) => validKidModulo(kid), { message: kidNotValidPath })
    .or(z.literal(''));

const OcrSchema = z.union([z.literal('required'), z.literal('error'), z.literal('not_required'), z.literal('init')]);
type OcrSchema = z.infer<typeof OcrSchema>;

const amountExceedsFundsPath: TranslationsFullPath = 'pay.validation.amount.noMoney';

export const PaymentSchema = z
    .object({
        kid: KidSchema,
        maxAmount: z.number().min(1),
        amount: z.number().min(1),
        payeeAccountNumber: PayeeAccountNumberSchema,
        payeeName: z.string().nonempty(),
        ocrKid: OcrSchema,
    })
    .superRefine((val, ctx) => {
        if (val.ocrKid === 'required' && val.kid === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['kid'],
                message: kidRequiredPath,
            });
        }

        if (val.payeeAccountNumber !== '' && (val.ocrKid === 'error' || val.ocrKid === 'not_required')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['payeeAccountNumber'],
                message: payeeAccountNumberInvalidOcrPath,
            });
        }

        if (val.amount > 0 && val.amount + PayService.cost(val.amount) > val.maxAmount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['amount'],
                message: amountExceedsFundsPath,
            });
        }
    });

export type KbPaymentForm = z.infer<typeof PaymentSchema>;
